import React from 'react';
import * as D3 from 'd3';

import { inferUnit } from 'ff/debrief/utils';

interface Props {
    goalMin: number | null;
    goalMax: number | null;
    actualMin?: number;
    actualMax?: number;
    size?: 'sm' | 'lg';
    label?: string;
    goal?: string;
}

const ScoreRange = (props: React.PropsWithChildren<Props>) => {
    const { goalMin, goalMax, actualMin, actualMax, size, label, goal } = props;
    const svgRef = React.useRef<SVGSVGElement>(null);
    const [dimensions, setDimensions] = React.useState({
        width: 0,
        height: size === 'lg' ? 60 : 15,
    });

    React.useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width } = entry.contentRect;
                setDimensions(prev => ({ ...prev, width }));
            }
        });

        if (svgRef.current) {
            resizeObserver.observe(svgRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const height = dimensions.height;
    const bracketHeight = size === 'lg' ? 24 : 12;
    const padding = dimensions.width * 0.15;
    const fixedBarWidth = dimensions.width * 0.05; // Fixed width for out-of-bounds bars
    const edgeMargin = dimensions.width * 0.08; // Margin from the edge for out-of-bounds indicators

    // Check for open-ended ranges
    const isOpenMin = goalMin === null;
    const isOpenMax = goalMax === null;

    // Determine the domain for the scale
    let domainMin: number, domainMax: number;

    if (isOpenMin && isOpenMax) {
        // Both ends open - use actual values with padding
        domainMin =
            (actualMin ?? 0) -
            Math.abs((actualMax ?? 0) - (actualMin ?? 0)) * 0.2;
        domainMax =
            (actualMax ?? 0) +
            Math.abs((actualMax ?? 0) - (actualMin ?? 0)) * 0.2;
    } else if (isOpenMin) {
        // Only min is open
        domainMin =
            Math.min(actualMin ?? goalMax ?? 0, goalMax ?? 0) -
            Math.abs((goalMax ?? 0) - (actualMin ?? 0)) * 0.2;
        domainMax = goalMax ?? 0;
    } else if (isOpenMax) {
        // Only max is open
        domainMin = goalMin ?? 0;
        domainMax =
            Math.max(actualMax ?? goalMin ?? 0, goalMin ?? 0) +
            Math.abs((actualMax ?? 0) - (goalMin ?? 0)) * 0.2;
    } else {
        // Both ends closed
        domainMin = goalMin ?? 0;
        domainMax = goalMax ?? 0;
    }

    // Set up D3 scale
    const xScale = D3.scaleLinear()
        .domain([domainMin, domainMax])
        .range([padding, dimensions.width - padding])
        .clamp(false); // Allow values outside the domain

    // Determine if actual values are out of bounds
    const actualMinOutOfVisualRange =
        !isOpenMin && actualMin !== undefined && xScale(actualMin) < 0;
    const actualMaxOutOfVisualRange =
        !isOpenMax &&
        actualMax !== undefined &&
        xScale(actualMax) > dimensions.width;
    const bothBelowGoal =
        !isOpenMin &&
        actualMin !== undefined &&
        actualMax !== undefined &&
        actualMax < goalMin;
    const bothAboveGoal =
        !isOpenMax &&
        actualMin !== undefined &&
        actualMax !== undefined &&
        actualMin > goalMax;

    // Calculate bar positions based on out-of-bounds status
    let barX1: number, barX2: number;
    let useFixedWidth = false;

    if (bothBelowGoal) {
        // Case 3: Both values are too small
        useFixedWidth = true;
        barX1 = edgeMargin;
        barX2 = barX1 + fixedBarWidth;
    } else if (bothAboveGoal) {
        // Case 4: Both values are too large
        useFixedWidth = true;
        barX2 = dimensions.width - edgeMargin;
        barX1 = barX2 - fixedBarWidth;
    } else if (actualMinOutOfVisualRange && !actualMaxOutOfVisualRange) {
        // Case 1: Min is too small, max is within range
        barX1 = edgeMargin;
        barX2 = Math.min(xScale(actualMax), dimensions.width - padding);
    } else if (!actualMinOutOfVisualRange && actualMaxOutOfVisualRange) {
        // Case 2: Max is too large, min is within range
        barX1 = Math.max(xScale(actualMin), padding);
        barX2 = dimensions.width - edgeMargin;
    } else {
        // Normal case: both values are within visible range
        barX1 = xScale(actualMin);
        barX2 = xScale(actualMax);
    }

    const belowMinValue =
        !isOpenMin && actualMin < goalMin ? actualMin : goalMin;
    const aboveMaxValue =
        !isOpenMax && actualMax > goalMax ? actualMax : goalMax;
    let withinRangeValues = [
        !isOpenMin && actualMin < goalMin ? goalMin : actualMin,
        !isOpenMax && actualMax > goalMax ? goalMax : actualMax,
    ];

    // Check if entirely out of goal range
    if (!isOpenMax && actualMin > goalMax && actualMax > goalMax) {
        withinRangeValues = [goalMax, goalMax];
    } else if (!isOpenMin && actualMin < goalMin && actualMax < goalMin) {
        withinRangeValues = [goalMin, goalMin];
    }

    // Compute bracket paths
    const bracketTop = (height - bracketHeight) / 2;
    const bracketBottom = (height + bracketHeight) / 2;

    const leftBracketPath = `M ${padding} ${bracketTop} L ${padding} ${bracketBottom}`;
    const midpointBracketPath = `M ${dimensions.width / 2} ${bracketTop} L ${dimensions.width / 2} ${bracketBottom}`;
    const rightBracketPath = `M ${dimensions.width - padding} ${bracketTop} L ${dimensions.width - padding} ${bracketBottom}`;

    const actualMinPath = `M ${xScale(actualMin)} ${bracketTop + 5} L ${xScale(actualMin)} ${bracketBottom - 5}`;
    const actualMaxPath = `M ${xScale(actualMax)} ${bracketTop + 5} L ${xScale(actualMax)} ${bracketBottom - 5}`;

    // Format value with unit
    const unit = inferUnit(label || '', goal || '');
    const formatValue = (value: number) => {
        if (Math.abs(value) < 1) {
            // For values less than 1, show two decimal places
            return `${value.toFixed(2)}${unit}`;
        } else if (unit === '°') {
            // For degrees, show one decimal place
            return `${value.toFixed(1)}${unit}`;
        }
        return `${Math.round(value)}${unit}`;
    };

    // Calculate label positions based on bar positions
    const minLabelDistance = 60; // Minimum pixels between label centers
    const actualMinX =
        useFixedWidth || actualMinOutOfVisualRange ? barX1 : xScale(actualMin);
    const actualMaxX =
        useFixedWidth || actualMaxOutOfVisualRange ? barX2 : xScale(actualMax);
    const labelsWouldOverlap =
        Math.abs(actualMaxX - actualMinX) < minLabelDistance;

    // Determine which labels to show
    const showBothLabels = props.size === 'lg' && !labelsWouldOverlap;
    const showMinLabel =
        props.size === 'lg' &&
        (showBothLabels || actualMin === actualMax) &&
        actualMin !== actualMax;
    const showMaxLabel =
        props.size === 'lg' && showBothLabels && actualMin !== actualMax;

    return (
        <svg
            ref={svgRef}
            width="100%"
            height={height}
            style={{ overflow: 'hidden' }}
        >
            {/* Horizontal line connecting brackets */}
            <line
                x1={padding}
                y1={height / 2}
                x2={dimensions.width - padding}
                y2={height / 2}
                stroke="black"
                strokeWidth="1"
            />

            {/* Main bar - red if out of bounds, otherwise colored based on goal compliance */}
            {/* Render bar as tick if the actual value is a single point */}
            {actualMax === actualMin ? (
                <rect
                    x={Math.min(
                        Math.max(xScale(actualMin), edgeMargin),
                        dimensions.width - edgeMargin
                    )}
                    y={height / 2 - bracketHeight / 4}
                    height={bracketHeight / 2}
                    width="2px"
                    fill={
                        (actualMin > goalMin || goalMin === null) &&
                        (actualMin < goalMax || goalMax === null)
                            ? '#4CAF50'
                            : '#DA2F09'
                    }
                />
            ) : useFixedWidth ||
              actualMinOutOfVisualRange ||
              actualMaxOutOfVisualRange ? (
                <>
                    {/* Out of bounds - render red bar at calculated position */}
                    <Rect
                        x1={barX1}
                        x2={barX2}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#DA2F09"
                    />
                    {/* Render green bar for in range values */}
                    <Rect
                        x1={xScale(withinRangeValues[0])}
                        x2={xScale(withinRangeValues[1])}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#4CAF50"
                    />
                </>
            ) : (
                <>
                    {/* Green bar from min to actual value (if within range) */}
                    <Rect
                        x1={xScale(withinRangeValues[0])}
                        x2={xScale(withinRangeValues[1])}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#4CAF50"
                    />

                    {/* Warning bar from actual min to goal min (if exceeding) */}
                    <Rect
                        x1={xScale(belowMinValue)}
                        x2={xScale(Math.min(actualMax, goalMin))}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#DA2F09"
                    />

                    {/* Warning bar from goal max to actual max (if exceeding) */}
                    <Rect
                        x1={xScale(Math.max(actualMin, goalMax))}
                        x2={xScale(aboveMaxValue)}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#DA2F09"
                    />
                </>
            )}

            {/* Min value label - only show if not open-ended */}
            {props.size === 'lg' && !isOpenMin && (
                <text
                    x={padding}
                    y={height / 2 + bracketHeight / 2 + 15}
                    textAnchor="middle"
                    fontSize="12"
                    fill="black"
                >
                    {formatValue(goalMin)}
                </text>
            )}

            {/* Max value label - only show if not open-ended */}
            {props.size === 'lg' && !isOpenMax && (
                <text
                    x={dimensions.width - padding}
                    y={height / 2 + bracketHeight / 2 + 15}
                    textAnchor="middle"
                    fontSize="12"
                    fill="black"
                >
                    {formatValue(goalMax)}
                </text>
            )}

            {actualMin === actualMax && (
                <>
                    <text
                        x={Math.min(
                            Math.max(xScale(actualMin), edgeMargin),
                            dimensions.width - edgeMargin
                        )}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMin)}
                    </text>
                </>
            )}

            {/* Actual min label */}
            {showMinLabel && (
                <>
                    <text
                        x={Math.max(actualMinX, edgeMargin)}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMin)}
                    </text>
                    {!useFixedWidth && !actualMinOutOfVisualRange && (
                        <path
                            d={actualMinPath}
                            stroke="black"
                            strokeWidth="1"
                            fill="none"
                        />
                    )}
                </>
            )}

            {/* Actual max label */}
            {showMaxLabel && (
                <>
                    <text
                        x={Math.min(actualMaxX, dimensions.width - edgeMargin)}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMax)}
                    </text>
                    {!useFixedWidth && !actualMaxOutOfVisualRange && (
                        <path
                            d={actualMaxPath}
                            stroke="black"
                            strokeWidth="1"
                            fill="none"
                        />
                    )}
                </>
            )}

            {/* Combined label when values are too close */}
            {props.size === 'lg' &&
                labelsWouldOverlap &&
                actualMin !== actualMax && (
                    <text
                        x={(actualMinX + actualMaxX) / 2}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMin)} &nbsp;&nbsp;{' '}
                        {formatValue(actualMax)}
                    </text>
                )}

            {/* Left bracket - only show if not open-ended */}
            {!isOpenMin && (
                <path
                    d={leftBracketPath}
                    stroke="black"
                    strokeWidth="1"
                    fill="none"
                />
            )}

            {/* mid-point bracket - only show if not open-ended */}
            {!isOpenMin && !isOpenMax && (
                <path
                    d={midpointBracketPath}
                    stroke="black"
                    strokeWidth="1"
                    fill="none"
                />
            )}

            {/* Right bracket - only show if not open-ended */}
            {!isOpenMax && (
                <path
                    d={rightBracketPath}
                    stroke="black"
                    strokeWidth="1"
                    fill="none"
                />
            )}
        </svg>
    );
};

const Rect = (
    props: React.PropsWithChildren<
        { x1: number; x2: number } & React.SVGProps<SVGRectElement>
    >
) => {
    const { x1, x2, children, ...rectProps } = props;
    const width = Math.abs(x2 - x1);

    return (
        <rect {...rectProps} x={x1} y={rectProps.y || 0} width={width}>
            {children}
        </rect>
    );
};

export default ScoreRange;
